import _ from 'lodash'
import { put, call, select, fork } from 'redux-saga/effects'

import { d } from 'core/service/lib/decimal'
import { round } from 'core/service/lib/number-format'
import { partnerApiRequest } from 'core/service-wrapper/partner-api/handler-request/service-request'
import { getIdentityUser } from 'identity/selectors'
import { partnerRequesting, updateFormState } from 'quick-quote/actions'
import { getModelFactors } from 'quick-quote/product-common/coverage-plan/selectors'
import { updateLumpSums } from 'quick-quote/product-investment/coverage-plan/actions'
import * as productInvestmentCoverageSelector from 'quick-quote/product-investment/coverage-plan/selectors'
import { updateQuestionnaire } from 'quick-quote/product-investment/risk-questionnaire/actions'
import * as productSelectionSelector from 'quick-quote/product-selection/selectors'

/**
 *
 * @param productCommonService {ReturnType<import('./product-common').createProductCommonService>}
 */
export function createProductInvestmentService(productCommonService) {
  function* loadRPQQuestionnaire() {
    const user = yield select(getIdentityUser)
    const questionnaires = yield call(partnerApiRequest.getRPQQuestionnaire, user)
    yield put(updateQuestionnaire(questionnaires))
  }

  function* getSellableFunds(userAgentType, displayProductQuery) {
    const params = {
      productRefName: displayProductQuery.productRefName,
      basicPlanCode: displayProductQuery.basicPlanCode,
      agentType: userAgentType,
    }
    if (_.isEmpty(params.basicPlanCode) || _.isEmpty(params.productRefName)) {
      return []
    }

    const user = yield select(getIdentityUser)
    const funds = yield call(partnerApiRequest.getProductFund, user, params)
    return funds
  }

  function* onSelectedModalFactor() {
    yield put(updateFormState({ dirty: true }))
    const selectedModelFactorID = yield select(productInvestmentCoverageSelector.getSelectedModelFactorID)
    const prevModelFactorID = yield select(productInvestmentCoverageSelector.getPreviousModelFactorID)
    const displayProductCode = yield select(productSelectionSelector.getSelectedDisplayProductCode)

    let regularPremium = (yield select(productInvestmentCoverageSelector.getRegularPremium)).value
    let regularTopup = undefined
    if (prevModelFactorID !== selectedModelFactorID) {
      const modalFactors = yield select(getModelFactors)

      const prevModalFactor = modalFactors.find((m) => m.id === prevModelFactorID)
      const selectedModalFactor = modalFactors.find((m) => m.id === selectedModelFactorID)

      regularPremium = round(
        d(regularPremium)
          .mul(d(prevModalFactor.periods))
          .div(d(selectedModalFactor.periods))
          .toNumber(),
        0
      )

      if (displayProductCode === 'RPUL') {
        regularTopup = (yield select(productInvestmentCoverageSelector.getRegularTopUp)).value

        regularTopup = round(
          d(regularTopup)
            .mul(d(prevModalFactor.periods))
            .div(d(selectedModalFactor.periods))
            .toNumber(),
          0
        )
      }
    }

    yield put(partnerRequesting('change_model_factor'))
    yield fork(productCommonService.calculateWithValidation, {
      RPP: regularPremium,
      modelFactor: selectedModelFactorID,
      RTU: regularTopup,
    })
  }

  function* onEditRegularPremium() {
    yield put(updateFormState({ dirty: true }))
    const regularPremium = yield select(productInvestmentCoverageSelector.getRegularPremium)
    yield put(partnerRequesting('change_regular_premium'))
    yield fork(productCommonService.calculateWithValidation, { RPP: regularPremium.value, sumAssured: null })
  }

  function* onEditRegularTopUp() {
    yield put(updateFormState({ dirty: true }))
    const regularTopUp = yield select(productInvestmentCoverageSelector.getRegularTopUp)
    yield put(partnerRequesting('change_regular_topup'))
    yield fork(productCommonService.calculateWithValidation, { RTU: regularTopUp.value })
  }

  function* onEditSumAssured() {
    yield put(updateFormState({ dirty: true }))
    const sumAssured = yield select(productInvestmentCoverageSelector.getSumAssured)
    yield put(partnerRequesting('change_sum_assured'))
    yield fork(productCommonService.calculateWithValidation, { sumAssured: sumAssured.value })
  }

  /**
   * onEditLumpSum
   * @param action {{payload: {year: number, value: number}}}
   */
  function* onEditLumpSum(action) {
    const { year, value } = action.payload
    yield put(updateFormState({ dirty: true }))
    const expectedReturn = yield select(productInvestmentCoverageSelector.getExpectedReturnComparisons)
    const expectedReturnError = yield select(productInvestmentCoverageSelector.getLumpSumErrors)
    const updateLumpSumYear = expectedReturn.find((item) => item.year === year)
    if (updateLumpSumYear) {
      yield put(
        updateLumpSums({
          values: [{ ...updateLumpSumYear, value }],
          errors: expectedReturnError,
        })
      )
    }

    yield put(partnerRequesting('change_lumpsum'))
    yield fork(productCommonService.calculateWithValidation, { LSTU: value })
  }

  return {
    loadRPQQuestionnaire,
    getSellableFunds,
    onSelectedModalFactor,
    onEditRegularTopUp,
    onEditSumAssured,
    onEditLumpSum,
    onEditRegularPremium,
  }
}
